import { LanguageCode } from "../../customers/customerlanguages";
import { LangStringObject } from "../../vocabulary/LangString";
export type GptModelConfig = { [key: string]: any };

export enum GptProviders {
  OPENAI = "OPENAI",
  AI_SWEDEN = "AI_SWEDEN",
  GOOGLE = "GOOGLE",
  ANTHROPIC = "ANTHROPIC",
  OPENROUTER = "OPENROUTER",
}

export interface GptModel {
  display_name: string; // ChatGPT
  provider: GptProviders;
  model_name: string; // gpt-3.5-turbo
  supports_images: boolean; // If we should display the image url input
  config?: GptModelConfig;
  default_selected?: boolean; // If the model is the default selected model in the Prompt Designer
  supported_parameters?: string[]; // List of supported parameters for OpenRouter models
}

export enum ParameterType {
  FLOAT = "float",
  INT = "int",
  STRING = "string",
}

export interface NumberParameter {
  min: number;
  max: number;
  step: number;
  type: ParameterType;
  label: string;
  caption?: string;
}
export interface StringParameter {
  type: ParameterType;
  label: string;
  caption?: string;
  options?: string[];
}

export type ParameterConfigMap = {
  [parameterName: string]: NumberParameter | StringParameter;
};

export const PARAMETER_CONFIGS: ParameterConfigMap = {
  reasoning: {
    type: ParameterType.STRING,
    label: "Reasoning Effort",
    options: ["none", "low", "medium", "high"],
    caption:
      "Controls the level of reasoning the model will do. Higher values will make the model think more deeply and produce more detailed responses.",
  },
  temperature: {
    min: 0,
    max: 2,
    step: 0.01,
    type: ParameterType.FLOAT,
    label: "Temperature",
    caption:
      "Influences the variety in the model's responses. Lower values lead to more predictable and typical responses, while higher values encourage more diverse and less common responses. At 0, the model always gives the same response for a given input.",
  },
  top_p: {
    min: 0,
    max: 1,
    step: 0.01,
    type: ParameterType.FLOAT,
    label: "Top P",
    caption:
      "Limits the model's choices to a percentage of likely tokens: only the top tokens whose probabilities add up to P. A lower value makes the model's responses more predictable, while the default setting allows for a full range of token choices.",
  },
  top_k: {
    min: 0,
    max: 100,
    step: 1,
    type: ParameterType.INT,
    label: "Top K",
    caption:
      "Limits the model's choice of tokens at each step, making it choose from a smaller set. A value of 1 means the model will always pick the most likely next token, leading to predictable results.",
  },
  frequency_penalty: {
    min: -2.0,
    max: 2.0,
    step: 0.01,
    type: ParameterType.FLOAT,
    label: "Frequency Penalty",
    caption:
      "Controls the repetition of tokens based on how often they appear in the input. It tries to use less frequently those tokens that appear more in the input, proportional to how frequently they occur. Negative values will encourage token reuse.",
  },
  presence_penalty: {
    min: -2.0,
    max: 2.0,
    step: 0.01,
    type: ParameterType.FLOAT,
    label: "Presence Penalty",
    caption:
      "Adjusts how often the model repeats specific tokens already used in the input. Higher values make such repetition less likely, while negative values do the opposite. Negative values will encourage token reuse.",
  },
  repetition_penalty: {
    min: 0,
    max: 2,
    step: 0.01,
    type: ParameterType.FLOAT,
    label: "Repetition Penalty",
    caption:
      "Helps to reduce the repetition of tokens from the input. A higher value makes the model less likely to repeat tokens, but too high a value can make the output less coherent.",
  },
  min_p: {
    min: 0,
    max: 1,
    step: 0.01,
    type: ParameterType.FLOAT,
    label: "Min P",
    caption:
      "Represents the minimum probability for a token to be considered, relative to the probability of the most likely token. If your Min-P is set to 0.1, that means it will only allow for tokens that are at least 1/10th as probable as the best possible option.",
  },
  top_a: {
    min: 0,
    max: 1,
    step: 0.01,
    type: ParameterType.FLOAT,
    label: "Top A",
    caption:
      "Consider only the top tokens with 'sufficiently high' probabilities based on the probability of the most likely token. Think of it like a dynamic Top-P. A lower Top-A value focuses the choices based on the highest probability token but with a narrower scope.",
  },
};

export enum SliderPopupTexts {
  MAX_TOKENS = "The maximum number of tokens returned in generated texts. A token corresponds to roughly 4 characters.",
  TEMPERATURE = "Higher values means the model will take more risks. Try 0.9 for more creative applications, and 0 for ones with a well-defined answer.",
  PRESENCE_PENALTY = "Number between -2.0 and 2.0. Increasing the model's likelihood to talk about new topics.",
  FREQUENCY_PENALTY = "Number between -2.0 and 2.0. Decreasing the model's likelihood to repeat the same line verbatim.",
}

export const PROMPT_WITH_NO_TAG_KEY = "no tags";

/**
 * Structure example:
 * {
 *  "gpt-4o": {
 *    "max_tokens": 1024
 *  },
 *  "gpt-3.5-turbo": {
 *    "max_tokens": 512
 *  }
 * }
 */
export type PromptConfigOverwrite = {
  [key: string]: {
    [key: string]: any;
  };
};

export type CopyAssistantLangStringTemplateLabels = {
  name: string;
  description: string;
  label_id: number;
  default: boolean;
};

export type CopyAssistantLangStringTemplate = {
  template_id: number;
  name: string;
  labels: CopyAssistantLangStringTemplateLabels[];
};

export enum PromptFormMode {
  UPDATE = "update",
  CREATE = "create",
  COPY = "copy",
}

export type PromptGroup = {
  name: string;
  prompt_ids: number[];
  tags?: string[];
  id: number;
};

export interface FurtherInstructionsGpt3 {
  input: string;
  output: string;
}

export type WebSearchDomainMode = "exclude" | "include";

export type WebSearchEngine = "native" | "exa" | "firecrawl" | "parallel";

export type WebSearchContextSize = "low" | "medium" | "high";

export type WebSearchConfig = {
  engine?: WebSearchEngine;
  search_prompt?: string;
  max_results?: number;
  search_context_size?: WebSearchContextSize;
  domains?: string[];
  domain_mode?: WebSearchDomainMode;
};

export type Prompt = {
  id: number;
  customer_id: number | null;
  name: string;
  language: LanguageCode | null;
  keep_html: boolean | null;
  model_name: string;
  model_provider: GptProviders;
  instruction: string;
  further_instructions: string[] | FurtherInstructionsGpt3[];
  examples_preface: string;
  config_overwrite: PromptConfigOverwrite;
  template_vocabulary_id: number | null;
  template_labels: number[];
  tags: string[];
  channel_id: number | null;
  fieldset_id?: number | null;
  document_section_id: number | null;
  is_translation?: boolean;
  include_documents_image?: boolean;
  use_web_search?: boolean;
  web_search_config?: WebSearchConfig;
  use_tm_file_search?: boolean;
};

export type CreatePrompt = Omit<
  Prompt,
  "id" | "customer_id" | "model_provider"
>;
export interface ObjectOf<T> {
  [key: string]: T;
}

export interface HasTags {
  tags?: string[];
}

export type SentenceGenerationInfo = {
  jobId?: number;
  promptId?: number;
  instruction?: string;
  inputText?: string;
  groupId?: number;
  autoTranslateAfterGeneration?: boolean;
  mtConfigGroupId?: number;
};

export type SentenceTemplateInfo = {
  tagId: string;
  langStringValues: LangStringObject;
  templateDisplayName: string;
  templateId: number;
};

export type SentenceSectionInfo = {
  documentSectionId: number;
  documentSectionName: string;
  textBlockId?: string;
  langStringValues?: LangStringObject;
  connectedChannels?: { name: string; id: number }[];
  active?: boolean;
};
export type Sentence = {
  id: string;
  value: LangStringObject;
  language?: LanguageCode;
  sentenceGroupId?: string;
  isLoading?: boolean;
  isStreaming?: boolean;
  loadingReason?: string;
  generationInfo?: SentenceGenerationInfo;
  templateInfo?: SentenceTemplateInfo;
  sectionInfo?: SentenceSectionInfo;
  prompt_id?: number;
  languagesToTranslate?: LanguageCode[];
};

export type RewritePrompt = {
  id: number;
  name: string;
  customer_id: number | null;
  model_name: string;
  instruction: string;
  order: number;
  params: PromptConfigOverwrite;
};

export type CreateRewritePrompt = Omit<RewritePrompt, "id" | "customer_id">;
